"use client";
import React from "react";
import { Box, Container, Typography, Chip, Stack, Button } from "@mui/material";
import Image from "next/image";
import "../app/globals.css";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { keyframes } from "@mui/system";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../constants/images";

const arrowBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
`;

const SocialProof = ({ hideCTA = false }) => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      sx={{
        backgroundImage: `url('${IMAGES.SOCIAL_PROOF_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: { xs: 4, sm: 5, md: 5, lg: 5 },
        ml: { xs: "-2%", sm: "-2%", md: "0" },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl" sx={{ mb: { xs: 3, sm: 4, md: "2%" } }}>
        {/* Top Section */}
        <Box
          textAlign="center"
          mb={{ xs: 4, sm: 5, md: 6, lg: 8 }}
          px={{ xs: 2, sm: 3, md: 0 }}
        >
          <Chip
            label={t("socialProof.chip")}
            sx={{
              backgroundColor: "white",
              border: "1px solid #0206174D",
              mb: { xs: 1.5, sm: 2 },
              color: "#818E98",
              fontSize: { xs: "12px", sm: "12px", md: "14px", lg: "16px" },
              fontWeight: 500,
              textTransform: "capitalize",
              px: { sm: 0.4, md: 0.5, lg: 0.6 },
              py: { xs: 0.1, lg: 0.7 },
              fontFamily: "var(--font-outfit)",
              borderRadius: "40px",
              height: "auto",
              "& .MuiChip-label": {
                whiteSpace: "normal",
                textOverflow: "clip",
                overflow: "visible",
                display: "block",
                paddingTop: "8px",
                paddingBottom: "8px",
              },
            }}
          />

          {/* <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 1.3,
              color: "#1e293b",
              fontSize: {
                xs: "1.8rem",
                sm: "2.3rem",
                md: "2.3rem",
                lg: "2.3rem",
                xl: "3.4rem",
              },
              lineHeight: 1.2,
              fontFamily: "var(--font-plusjakarta)",
              maxWidth: 1050,
              mx: "auto",
              textAlign: { xs: isArabic ? "center" : "left", md: "center" },
            }}
          >
            {t("socialProof.title1")}
            <span
              style={{ color: "#775CE5", marginLeft: "1%", marginRight: "1%" }}
            >
              {t("socialProof.titleHighlight1")}
            </span>
            {t("socialProof.title2")}
            <span style={{ color: "#775CE5", marginLeft: "1%" }}>
              {t("socialProof.titleHighlight2")}
            </span>
          </Typography> */}
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 1.3,
              color: "#1e293b",
              fontSize: {
                xs: "1.8rem",
                sm: "2.3rem",
                md: "2.3rem",
                lg: "2.3rem",
                xl: "3.4rem",
              },
              lineHeight: isArabic ? { xs: 1.2, md: 1.3 } : 1.2,
              fontFamily: "var(--font-plusjakarta)",
              maxWidth: 1050,
              mx: "auto",
              textAlign: { xs: isArabic ? "center" : "left", md: "center" },
            }}
          >
            {t("socialProof.title1")}{" "}
            <span style={{ color: "#775CE5" }}>
              {t("socialProof.titleHighlight1")}
            </span>{" "}
            {t("socialProof.title2")}{" "}
            <span style={{ color: "#775CE5" }}>
              {t("socialProof.titleHighlight2")}
            </span>
          </Typography>
          <Typography
            sx={{
              color: "#02061799",
              mb: { xs: 1, sm: 1.2, md: 1.5 },
              fontWeight: 400,
              fontSize: {
                xs: "14px",
                sm: "15px",
                md: "18px",
                lg: "18px",
                xl: "24px",
              },
              fontFamily: "var(--font-outfit)",
              mx: "auto",
              textAlign: "center",
              maxWidth: { xs: "100%", lg: 900 },
              mt: { xs: "2%", md: "1.5%" },
              lineHeight: 1.4,
            }}
          >
            {t("socialProof.subtitle")}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ mt: { xs: "2%", md: "1.3%" } }}
          >
            <Image
              src={IMAGES.RATING_STARS}
              alt="5 stars"
              width={100}
              height={20}
            />
            <Typography
              sx={{
                fontSize: {
                  xs: "13px",
                  sm: "15px",
                  md: "18px",
                  lg: "18px",
                  xl: "21px",
                },
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
                color: "#02061799",
              }}
            >
              {t("socialProof.rating")}
            </Typography>
          </Box>
        </Box>

        {/* Testimonial Grid - Top Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2.2fr 1fr" },
            gap: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 2, sm: 2.5, md: 3 },
            maxWidth: { xs: "100%", lg: "lg" },
            mx: { xs: 0, lg: "auto" },
            width: "100%",
            px: { xs: 2, sm: 3, md: 2, lg: 2, xl: 0 },
          }}
        >
          {/* Card 1 - Image Testimonial */}
          <Box
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              position: "relative",
              height: { xs: "280px", sm: "320px", md: "380px", lg: "400px" },
            }}
          >
            <Box sx={{ position: "relative", width: "100%", height: "120%" }}>
              <Image
                src={IMAGES.RAMEEZ_KHAN}
                alt="Rameez Khan feedback on best recruiting automation software"
                fill
                style={{ objectFit: "cover" }}
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(to left, rgba(0,0,0,0.4), transparent 40%)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: { xs: 2, sm: 2.5, md: 3, lg: 4 },
                alignItems: isArabic ? "flex-start" : "flex-end",
              }}
            >
              <Typography
                sx={{
                  color: "white",
                  fontSize: {
                    xs: "13px",
                    sm: "14px",
                    md: "17px",
                    lg: "18px",
                    xl: "20px",
                  },
                  fontWeight: 400,
                  fontFamily: "var(--font-outfit)",
                  lineHeight: 1.5,
                  maxWidth: { xs: "100%", md: "55%", lg: "49%" },
                  textAlign: isArabic ? "right" : "left",
                  mt: { xs: "3%", md: "2%" },
                  ml: { xs: "37%", md: "0%" },
                }}
              >
                {t("socialProof.card1.text")}
              </Typography>
              <Typography
                sx={{
                  color: "white",
                  fontSize: {
                    xs: "13px",
                    sm: "14px",
                    md: "16px",
                    lg: "17px",
                    xl: "18px",
                  },
                  fontWeight: 500,
                  fontFamily: "var(--font-outfit)",
                  textAlign: "right",
                }}
              >
                {t("socialProof.card1.name")}
              </Typography>
              <Typography
                sx={{
                  mt: { xs: "-14%", md: "-14%", lg: "-14%", xl: "-12%" },
                  color: "white",
                  fontSize: {
                    xs: "12px",
                    md: "12px",
                    lg: "12px",
                    xl: "13.5px",
                  },
                  fontWeight: 300,
                  fontFamily: "var(--font-outfit)",
                  textAlign: "right",
                }}
              >
                {t("socialProof.card1.role")}
              </Typography>
            </Box>
          </Box>

          {/* Card 2 - Stats */}
          <Box
            sx={{
              bgcolor: "#6CCDEE",
              borderRadius: 4,
              p: { xs: 2.5, sm: 3, md: 3.5, lg: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: { xs: "280px", sm: "320px", md: "380px", lg: "400px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: { xs: 3, sm: 4, md: 5 },
              }}
            >
              <Box
                sx={{
                  width: { xs: 32, md: 45 },
                  height: { xs: 32, md: 45 },
                  borderRadius: "50%",
                  overflow: "hidden",
                }}
              >
                <Image
                  src={IMAGES.HUMNA_AVATAR}
                  alt="Humna Shafqat review"
                  width={60}
                  height={60}
                  style={{ objectFit: "cover", width: "100%", height: "100%" }}
                />
              </Box>
              <Box sx={{ ml: "5%", mr: "7%" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: {
                      xs: "14px",
                      md: "14px",
                      lg: "14px",
                      xl: "16px",
                    },
                    color: "white",
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {t("socialProof.card2.name")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: {
                      xs: "12px",
                      md: "12px",
                      lg: "12px",
                      xl: "13.5px",
                    },
                    color: "white",
                    fontFamily: "var(--font-outfit)",
                    mt: 0.1,
                    fontWeight: 300,
                  }}
                >
                  {t("socialProof.card2.role")}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: {
                  xs: "45px",
                  sm: "55px",
                  md: "70px",
                  lg: "70px",
                  xl: "80px",
                },
                fontWeight: 400,
                color: "#FFFFFF",
                lineHeight: 1,
                mt: "-2%",
                fontFamily: "var(--font-hindguntur)",
              }}
            >
              {t("socialProof.card2.percent")}
              <Box
                component="span"
                sx={{
                  fontSize: { xs: "26px", sm: "32px", md: "36px", lg: "40px" },
                }}
              >
                %
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: {
                  xs: "16px",
                  sm: "18px",
                  md: "20px",
                  lg: "20px",
                  xl: "25px",
                },
                fontWeight: 400,
                color: "#FFFFFF",
                lineHeight: 1.4,
                fontFamily: "var(--font-outfit)",
              }}
            >
              {t("socialProof.card2.text")}
            </Typography>
          </Box>
        </Box>

        {/* Bottom Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 2.2fr" },
            gap: { xs: 2, sm: 2.5, md: 3 },
            maxWidth: { xs: "100%", lg: "lg" },
            mx: { xs: 0, lg: "auto" },
            width: "100%",
            px: { xs: 2, sm: 3, md: 2, lg: 2, xl: 0 },
          }}
        >
          {/* Card 3 - Green */}
          <Box
            sx={{
              bgcolor: "#236D2A",
              borderRadius: 4,
              p: { xs: 2.5, sm: 3, md: 3.5, lg: 4 },
              height: { xs: "auto", sm: "300px", md: "330px", lg: "350px" },
              minHeight: { xs: "280px" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: { xs: 2, sm: 2.5, md: 3 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 32, md: 60 },
                    height: { xs: 32, md: 50 },
                    borderRadius: "50%",
                    overflow: "hidden",
                  }}
                >
                  <Image
                    src={IMAGES.SAJID_AVATAR}
                    alt="Sajid Sharif testimonial"
                    width={60}
                    height={60}
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Box>
                <Box sx={{ ml: "5%", mr: "7%" }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: {
                        xs: "14px",
                        md: "15px",
                        lg: "15px",
                        xl: "16px",
                      },
                      color: "white",
                      fontFamily: "var(--font-outfit)",
                    }}
                  >
                    {t("socialProof.card3.name")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "12px",
                        md: "12px",
                        lg: "12px",
                        xl: "13.5px",
                      },
                      color: "white",
                      fontFamily: "var(--font-outfit)",
                      mt: 0.1,
                      fontWeight: 300,
                    }}
                  >
                    {t("socialProof.card3.role")}
                  </Typography>
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: {
                    xs: "14px",
                    sm: "15px",
                    md: "17px",
                    lg: "17px",
                    xl: "18px",
                  },
                  fontWeight: 400,
                  color: "#FFFF",
                  lineHeight: 1.5,
                  fontFamily: "var(--font-outfit)",
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {t("socialProof.card3.text")}
              </Typography>
            </Box>
          </Box>

          {/* Card 4 - Orange */}
          <Box
            sx={{
              bgcolor: "#FFA600",
              borderRadius: 4,
              p: { xs: 2.5, sm: 3, md: 3.5, lg: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: { xs: "auto", sm: "300px", md: "330px", lg: "350px" },
              minHeight: { xs: "280px" },
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: "18px",
                  sm: "20px",
                  md: "24px",
                  lg: "25px",
                  xl: "28px",
                },
                fontWeight: 400,
                color: "#FFFFFF",
                lineHeight: 1.4,
                fontFamily: "var(--font-outfit)",
                maxWidth: { xs: "100%", lg: 700 },
                mb: { xs: 2, md: 0 },
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("socialProof.card4.text")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: { xs: 32, md: 45 },
                  height: { xs: 32, md: 45 },
                  borderRadius: "50%",
                  overflow: "hidden",
                }}
              >
                <Image
                  src={IMAGES.MAHA_AVATAR}
                  alt="Maha Sohail success story"
                  width={60}
                  height={60}
                  style={{ objectFit: "cover", width: "100%", height: "100%" }}
                />
              </Box>
              <Box sx={{ ml: "2%", mr: "3%" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: {
                      xs: "14px",
                      md: "14px",
                      lg: "14px",
                      xl: "16px",
                    },
                    color: "white",
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {t("socialProof.card4.name")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: {
                      xs: "12px",
                      md: "12px",
                      lg: "12px",
                      xl: "13.5px",
                    },
                    color: "white",
                    fontFamily: "var(--font-outfit)",
                    mt: 0.1,
                    fontWeight: 300,
                  }}
                >
                  {t("socialProof.card4.role")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SocialProof;
